import { HttpEventType, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '@core/services/auth/auth.service';
import { fluxLogger } from '@core/services/logger/logger.instance';
import { environment } from '@environment';
import { tap } from 'rxjs';

const WHITELISTED_URLS = [
  new URL(environment.BASE_URL).hostname,
  new URL(environment.ISENGARD_URL).hostname,
  new URL(environment.ISENGARD_REALTIME_URL).hostname,
  new URL(environment.API_BRADESCO_URL).hostname,
];

const attachmentsHostname = 'attachments.fluxcontrol.com.br';

const logger = fluxLogger.extend('network').extend('request');

export function authInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn) {
  const authenticatedUser = inject(AuthService).authenticatedUser();

  if (!authenticatedUser?.token) {
    return next(req);
  }

  // Skip the interceptor for specific URLs
  if (req.url.includes(attachmentsHostname)) {
    return next(req);
  }

  let hostname: string;
  try {
    const baseUrl = window.location.origin; // Use the current origin as the base URL
    hostname = new URL(req.url, baseUrl).hostname;
  } catch (error) {
    logger.error('Invalid URL in request:', req.url, error);
    return next(req); // Skip processing if the URL is invalid
  }

  if (!WHITELISTED_URLS.some((url) => hostname.includes(url))) {
    return next(req);
  }

  const startTime: number = logger.enabled() ? performance.now() : 0;

  logger.debug('Intercepting request.', {
    url: req.url,
    method: req.method,
    headers: req.headers,
  });

  const newReq = req.clone({
    headers: req.headers
      .append('x-graphql-client-name', environment.GRAPHQL_CLIENT_NAME)
      .append('x-graphql-client-version', environment.GRAPHQL_CLIENT_VERSION)
      .append('Authorization', authenticatedUser.token)
      .append('Apollo-Require-Preflight', 'true')
      .append('Content-Type', 'application/json'),
  });

  if (['development', 'local-production'].includes(environment.ENVIRONMENT)) {
    // referrer policy no-referrer-when-downgrade
    newReq.headers.append('Referrer-Policy', 'no-referrer-when-downgrade');
  }

  return next(newReq).pipe(
    tap({
      next: (response) => {
        if (response.type !== HttpEventType.Response) {
          return;
        }
        logger.debug('Request completed.', {
          url: req.url,
          method: req.method,
          headers: req.headers,
          response,
          requestId: response.headers.get('x-flux-request-id'),
          took: performance.now() - startTime,
        });
      },
      error: (error) => {
        logger.error('Request failed.', {
          url: req.url,
          method: req.method,
          headers: req.headers,
          error,
          took: performance.now() - startTime,
        });
      },
    }),
  );
}
